import { FilePlus2 } from 'lucide-react'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { Permission } from '@/features/roles/consts/permissions'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { useStartCreditProcess } from '../hooks/use-start-credit-process'
import {
  CREDIT_ROW_STATUS_COLORS,
  CREDIT_ROW_STATUS_LABELS,
  type CreditListRow,
  formatCreditLocalDate
} from '../models/credit-list'

/**
 * La negociación cerrada que TODAVÍA no tiene expediente, y el botón que se lo abre.
 *
 * ── POR QUÉ ACÁ Y NO EN UNA COLUMNA DE LA TABLA ──────────────────────────────
 *
 * Porque es como se hace en todo el resto del producto: la fila se clickea, el panel se abre, y las
 * acciones viven adentro (`side-panel-actions`, el molde que fija `CLAUDE.md`). La primera versión
 * puso un botón «Iniciar» por fila y quedaba fuera de convención — además de robarle ancho a la
 * tabla para una acción que sólo aplica a una parte de las filas.
 *
 * ── EL PANEL SE ACTUALIZA SOLO, Y ESO SIMPLIFICA EL ALTA ─────────────────────
 *
 * El panel se direcciona por la FILA (el `neg_`), no por el expediente: la fila existe antes y
 * después del alta. Así, cuando el listado se refresca y la fila pasa a traer su `credit`, el mismo
 * panel se vuelve la ficha del expediente sin tocar la URL ni reabrir nada.
 *
 * Eso es lo que hace innecesario el enredo de la versión anterior, que abría el panel escribiendo
 * un `creditId` desde el callback del alta — un callback que ni siquiera corría, porque el botón
 * vivía en una celda que desaparecía al triunfar la acción.
 */
export function CreditNotStartedPanel({ row }: { row: CreditListRow }) {
  const { has } = usePermissions()
  const startCredit = useStartCreditProcess()

  // `isSuccess` cuenta como «ya no ofrezcas la acción»: entre que el POST vuelve y que el listado
  // termina de refrescarse hay una ventana en la que la fila TODAVÍA dice `not_started`. Sin esto,
  // el botón sigue ahí, invitando a un segundo alta que se comería un `CREDIT_ALREADY_EXISTS`
  // sobre el expediente que uno mismo acaba de abrir.
  const busy = startCredit.isPending || startCredit.isSuccess

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Dónde va
              </p>
              <p className="mt-1 text-sm font-medium leading-5">
                El trámite todavía no arrancó. Se abre cuando administración lo decide, o solo al
                acercarse el último pago.
              </p>
            </div>
            <Badge
              variant="secondary"
              className={cn('shrink-0 border-0', CREDIT_ROW_STATUS_COLORS[row.status])}
            >
              {CREDIT_ROW_STATUS_LABELS[row.status]}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-2 rounded-md bg-secondary/70 p-2.5 text-xs">
            <div>
              <p className="text-muted-foreground">Inmueble</p>
              <p className="mt-0.5 truncate font-medium">{row.property?.name ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Proyecto</p>
              <ProjectBadge name={row.project?.name} code={row.project?.code} className="mt-0.5" />
            </div>
            <div>
              <p className="text-muted-foreground">Último pago</p>
              <p className="mt-0.5 font-medium tabular-nums">
                {formatCreditLocalDate(row.lastPaymentDate)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Inicio previsto</p>
              <p className="mt-0.5 font-medium tabular-nums">
                {formatCreditLocalDate(row.startsAt)}
              </p>
            </div>
          </div>

          {/*
           * La acción se ESCONDE sin permiso, no se deshabilita: un botón apagado sin explicación
           * se lee como «está roto». Es el mismo criterio del resto del producto.
           */}
          {has(Permission.StartCreditProcess) ? (
            <SidePanelActions
              actions={[
                {
                  label: busy ? 'Abriendo el expediente…' : 'Iniciar el trámite',
                  icon: <FilePlus2 aria-hidden="true" className="size-4" />,
                  variant: 'default',
                  primary: true,
                  disabled: busy,
                  onClick: () => {
                    startCredit.mutate({ negotiation: row.id })
                  }
                }
              ]}
            />
          ) : null}
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection separated={false}>
        <SidePanelSectionBody>
          <p className="text-xs text-muted-foreground">
            Al abrirlo se copia la versión publicada de la plantilla: las siete etapas y sus pasos
            quedan fijos para este trámite, aunque después se publique una versión nueva.
          </p>
        </SidePanelSectionBody>
      </SidePanelSection>
    </div>
  )
}
